import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./components/layout/AppShell";
import { AuthProvider } from "./auth/AuthContext";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { RoleGuard } from "./auth/RoleGuard";
import { LoginPage } from "./pages/LoginPage";
import { TrackingPage } from "./pages/TrackingPage";
import { FleetPage } from "./pages/FleetPage";
import { OrdersPage } from "./pages/OrdersPage";
import { DriversPage } from "./pages/DriversPage";
import { SupervisorsPage } from "./pages/SupervisorsPage";
import { AutoMessagesPage } from "./pages/AutoMessagesPage";
import { WarehousesPage } from "./pages/WarehousesPage";
import { VehiclesPage } from "./pages/VehiclesPage";
import { ClientsPage } from "./pages/ClientsPage";
import { AlertsPage } from "./pages/AlertsPage";
import { ReportsPage } from "./pages/ReportsPage";
import { SupervisorPage } from "./pages/SupervisorPage";
import { CoordinatorPage } from "./pages/CoordinatorPage";
import { ProgramacionPage } from "./pages/ProgramacionPage";
import { ProgramacionPlanPage } from "./pages/ProgramacionPlanPage";
import { ProgramacionBatchDetailPage } from "./pages/ProgramacionBatchDetailPage";
import { AdminPage } from "./pages/AdminPage";
import { LastMileImportPage } from "./pages/LastMileImportPage";

// Role matrix:
//   admin       — everything
//   coordinator — operación + maestros + alertas (no planning de rutas)
//   supervisor  — operación + maestros + alertas (idem)
//   driver      — no web; landing en /login con un mensaje (Fase 5 = app móvil)
//
// Maestros (drivers/warehouses/clients) están abiertos a los 3 roles web
// porque comparten una vista de consulta. Cuando llegue el CRUD real
// podemos cerrar la edición a admin via RoleGuard interno en cada page.

const WEB_ROLES = ["admin", "coordinator", "supervisor"] as const;
const PLANNING_ROLES = ["admin"] as const; // solo admin programa rutas en V1

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/tracking" replace />} />
            <Route
              path="/tracking"
              element={
                <RoleGuard roles={[...WEB_ROLES]}>
                  <TrackingPage />
                </RoleGuard>
              }
            />
            <Route
              path="/fleet"
              element={
                <RoleGuard roles={[...WEB_ROLES]}>
                  <FleetPage />
                </RoleGuard>
              }
            />
            <Route
              path="/orders"
              element={
                <RoleGuard roles={[...WEB_ROLES]}>
                  <OrdersPage />
                </RoleGuard>
              }
            />
            <Route
              path="/drivers"
              element={
                <RoleGuard roles={[...WEB_ROLES]}>
                  <DriversPage />
                </RoleGuard>
              }
            />
            <Route
              path="/supervisors"
              element={
                <RoleGuard roles={["admin", "coordinator"]}>
                  <SupervisorsPage />
                </RoleGuard>
              }
            />
            <Route
              path="/auto-messages"
              element={
                <RoleGuard roles={["admin", "coordinator"]}>
                  <AutoMessagesPage />
                </RoleGuard>
              }
            />
            <Route
              path="/warehouses"
              element={
                <RoleGuard roles={[...WEB_ROLES]}>
                  <WarehousesPage />
                </RoleGuard>
              }
            />
            <Route
              path="/vehicles"
              element={
                <RoleGuard roles={[...WEB_ROLES]}>
                  <VehiclesPage />
                </RoleGuard>
              }
            />
            <Route
              path="/clients"
              element={
                <RoleGuard roles={[...WEB_ROLES]}>
                  <ClientsPage />
                </RoleGuard>
              }
            />
            <Route
              path="/alerts"
              element={
                <RoleGuard roles={[...WEB_ROLES]}>
                  <AlertsPage />
                </RoleGuard>
              }
            />
            <Route
              path="/reports"
              element={
                <RoleGuard roles={[...WEB_ROLES]}>
                  <ReportsPage />
                </RoleGuard>
              }
            />
            <Route
              path="/supervisor"
              element={
                <RoleGuard roles={["admin", "supervisor", "coordinator"]}>
                  <SupervisorPage />
                </RoleGuard>
              }
            />
            <Route
              path="/coordinador"
              element={
                <RoleGuard roles={["admin", "coordinator"]}>
                  <CoordinatorPage />
                </RoleGuard>
              }
            />
            <Route
              path="/programacion"
              element={
                <RoleGuard roles={[...PLANNING_ROLES]}>
                  <ProgramacionPage />
                </RoleGuard>
              }
            />
            <Route
              path="/programacion/planificar"
              element={
                <RoleGuard roles={[...PLANNING_ROLES]}>
                  <ProgramacionPlanPage />
                </RoleGuard>
              }
            />
            <Route
              path="/programacion/batches/:id"
              element={
                <RoleGuard roles={[...PLANNING_ROLES]}>
                  <ProgramacionBatchDetailPage />
                </RoleGuard>
              }
            />
            <Route
              path="/admin/*"
              element={
                <RoleGuard roles={["admin"]}>
                  <AdminPage />
                </RoleGuard>
              }
            />
            <Route
              path="/ultima-milla/importar"
              element={
                <RoleGuard roles={["admin"]}>
                  <LastMileImportPage />
                </RoleGuard>
              }
            />
            <Route path="*" element={<Navigate to="/tracking" replace />} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
